import React, { useState } from 'react';

const DebateArenaGame = ({ gameData, onComplete }) => {
    const [selectedSide, setSelectedSide] = useState(null); // 'pro' or 'contra'
    const [currentArgumentIndex, setCurrentArgumentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [showCounter, setShowCounter] = useState(false);
    const [completed, setCompleted] = useState(false);

    const currentArguments = selectedSide ? gameData.sides[selectedSide].arguments : [];
    const currentArgument = currentArguments[currentArgumentIndex];

    const selectSide = (side) => {
        setSelectedSide(side);
        playSelectSound();
        if (navigator.vibrate) navigator.vibrate(60);
    };

    const viewCounter = () => {
        setShowCounter(true);
        if (navigator.vibrate) navigator.vibrate(40);
        setTimeout(() => {
            nextArgument();
        }, 4000);
    };

    const nextArgument = () => {
        const addedPoints = gameData.points_per_argument || 15;
        if (currentArgumentIndex < currentArguments.length - 1) {
            setCurrentArgumentIndex(prev => prev + 1);
            setShowCounter(false);
            setScore(prev => prev + addedPoints);
        } else {
            setScore(prev => prev + addedPoints);
            setCompleted(true);
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
        }
    };

    const playSelectSound = () => {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
            const ctx = new AudioContext();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.setValueAtTime(600, ctx.currentTime);
            osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1);
            gain.gain.setValueAtTime(0.1, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.2);
            osc.start();
            osc.stop(ctx.currentTime + 0.2);
        }
    };

    if (!gameData || !gameData.sides) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-purple-100 animate-pulse">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">🎤</div>
                <h3 className="text-base md:text-3xl font-black text-purple-900 tracking-tighter uppercase">Ruošiama debatų arena...</h3>
            </div>
        );
    }

    const progress = selectedSide ? ((currentArgumentIndex + (showCounter ? 1 : 0)) / currentArguments.length) * 100 : 0;

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Premium Header / Progress Area */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-gradient-to-r from-blue-900 via-indigo-900 to-purple-900 text-white">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner border border-white/20 animate-float">
                            🎙️
                        </div>
                        <div>
                            <h3 className="text-base md:text-3xl font-black tracking-tight leading-tight">
                                Debatų Arena
                            </h3>
                            <p className="text-indigo-200 font-bold opacity-80">
                                Išmok apginti savo nuomonę mandagiai ir tvirtai!
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="bg-white/10 px-6 py-3 rounded-2xl border border-white/20 shadow-inner">
                            <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest leading-none mb-1">Ritorika</p>
                            <p className="text-2xl font-black text-white leading-none">⭐ {score}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-6 w-full bg-white/10 rounded-full h-3 p-1 shadow-inner overflow-hidden border border-white/5">
                    <div 
                        className="h-full bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-500 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {/* Debate Topic Brief */}
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-10 shadow-xl border-white/60 bg-white/40 backdrop-blur-xl text-center relative overflow-hidden">
                <div className="absolute -top-4 md:p-10 -right-10 text-xl md:text-5xl md:text-[150px] opacity-[0.03] rotate-12">📢</div>
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.5em] mb-4">Debatų Tema</h4>
                <h3 className="text-base md:text-3xl md:text-lg md:text-4xl font-black text-indigo-900 tracking-tight relative z-10 leading-tight">
                    "{gameData.topic}"
                </h3>
            </div>

            {/* Arena Stage */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-16 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center min-h-[50vh] justify-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 opacity-5 text-2xl md:text-6xl md:text-[200px] font-black select-none pointer-events-none">🎤</div>
                <div className="absolute bottom-0 left-0 p-4 md:p-12 opacity-5 text-2xl md:text-6xl md:text-[200px] font-black select-none pointer-events-none">🏛️</div>

                <div className="w-full max-w-4xl relative z-10 text-center">
                    {/* Side Selection */}
                    {!selectedSide && !completed && (
                        <div className="animate-slide-up space-y-4 md:space-y-12">
                            <h3 className="text-lg md:text-4xl font-black text-slate-800 tracking-tighter uppercase mb-4">
                                PASIRINK SAVO PUSĘ
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:p-8">
                                {/* PRO Side */}
                                <button
                                    onClick={() => selectSide('pro')}
                                    className="premium-button-3d group p-4 md:p-10 bg-gradient-to-br from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 rounded-xl md:rounded-[3rem] border-4 border-emerald-400 hover:border-emerald-600 transition-all shadow-xl text-center flex flex-col items-center"
                                >
                                    <div className="text-8xl mb-6 group-hover:scale-110 transition-transform">👍</div>
                                    <h4 className="text-base md:text-3xl font-black text-emerald-700 mb-6 uppercase tracking-tight">
                                        {gameData.sides.pro.title}
                                    </h4>
                                    <div className="text-left space-y-3 bg-white/50 p-2 md:p-6 rounded-[2rem] border border-emerald-100 shadow-inner w-full">
                                        {gameData.sides.pro.arguments.slice(0, 2).map((arg, idx) => (
                                            <div key={idx} className="text-sm font-bold text-emerald-800 leading-tight">
                                                • {arg.argument.substring(0, 60)}...
                                            </div>
                                        ))}
                                    </div>
                                </button>

                                {/* CONTRA Side */}
                                <button
                                    onClick={() => selectSide('contra')}
                                    className="premium-button-3d group p-4 md:p-10 bg-gradient-to-br from-rose-50 to-orange-50 hover:from-rose-100 hover:to-orange-100 rounded-xl md:rounded-[3rem] border-4 border-rose-400 hover:border-rose-600 transition-all shadow-xl text-center flex flex-col items-center"
                                >
                                    <div className="text-8xl mb-6 group-hover:scale-110 transition-transform">👎</div>
                                    <h4 className="text-base md:text-3xl font-black text-rose-700 mb-6 uppercase tracking-tight">
                                        {gameData.sides.contra.title}
                                    </h4>
                                    <div className="text-left space-y-3 bg-white/50 p-2 md:p-6 rounded-[2rem] border border-rose-100 shadow-inner w-full">
                                        {gameData.sides.contra.arguments.slice(0, 2).map((arg, idx) => (
                                            <div key={idx} className="text-sm font-bold text-rose-800 leading-tight">
                                                • {arg.argument.substring(0, 60)}...
                                            </div>
                                        ))}
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Argument & Counter Interaction */}
                    {selectedSide && !completed && currentArgument && (
                        <div className="animate-slide-up space-y-4 md:space-y-12">
                            <div className="space-y-6">
                                <div className="inline-block bg-indigo-900/5 text-indigo-600 px-6 py-2 rounded-full text-xs font-black uppercase tracking-[0.4em] border border-indigo-100">Tavo Kalba</div>
                                <div className={`relative p-4 md:p-12 rounded-[3.5rem] shadow-2xl border-4 transition-all duration-500 overflow-hidden ${selectedSide === 'pro' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-rose-50 border-rose-200 text-rose-900'}`}>
                                    <div className="absolute -top-2 md:p-6 -left-6 text-9xl opacity-5 font-serif italic">"</div>
                                    <p className="text-base md:text-3xl md:text-lg md:text-4xl font-black leading-tight font-serif italic relative z-10">
                                        {currentArgument.argument}
                                    </p>
                                    <div className="absolute -bottom-10 -right-6 text-9xl opacity-5 font-serif italic">"</div>
                                </div>
                            </div>

                            {!showCounter ? (
                                <button
                                    onClick={viewCounter}
                                    className="premium-button-3d group w-full max-w-lg py-4 md:py-10 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl md:rounded-[3rem] font-black text-2xl shadow-2xl shadow-indigo-500/30 uppercase tracking-widest relative overflow-hidden"
                                >
                                    <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                                    <span className="relative z-10 flex items-center justify-center gap-4">
                                        IŠKLAUSYTI ATSAKYMĄ 🔄
                                    </span>
                                </button>
                            ) : (
                                <div className="animate-pop-in space-y-6">
                                    <div className="inline-block bg-blue-900/5 text-blue-600 px-6 py-2 rounded-full text-xs font-black uppercase tracking-[0.4em] border border-blue-100">Oponento Atsakas</div>
                                    <div className="p-4 md:p-10 bg-blue-50 border-4 border-blue-200 rounded-xl md:rounded-[3rem] shadow-xl relative group">
                                        <div className="absolute -top-4 -right-4 text-lg md:text-4xl animate-bounce">🗣️</div>
                                        <p className="text-2xl font-bold text-blue-900 leading-relaxed font-serif">
                                            {currentArgument.counter}
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-center gap-3 pt-6">
                                        <div className="w-3 h-3 bg-indigo-200 rounded-full animate-bounce"></div>
                                        <div className="w-3 h-3 bg-indigo-200 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                                        <div className="w-3 h-3 bg-indigo-200 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                                        <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Ruošiamasi kitam etapui...</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Completion Stage */}
                    {completed && (
                        <div className="animate-slide-up space-y-4 md:space-y-12">
                            <div className="text-2xl md:text-6xl md:text-[180px] animate-bounce drop-shadow-2xl">🏆</div>
                            <div className="space-y-4">
                                <h2 className="text-xl md:text-5xl font-black text-indigo-600 tracking-tighter uppercase">
                                    DEBATŲ MEISTRAS!
                                </h2>
                                <p className="text-xl font-bold text-slate-500">Nuostabus pasirodymas arenoje!</p>
                            </div>
                            
                            <div className="bg-indigo-50 p-4 md:p-12 rounded-2xl md:rounded-[4rem] border-4 border-indigo-100 shadow-inner max-w-2xl mx-auto space-y-6">
                                <p className="text-base md:text-3xl font-bold text-slate-700 leading-relaxed font-serif italic">
                                    "Tavo argumentai buvo stiprūs, o kontrargumentus išklausei su pagarba. Tai yra tikro lyderio bruožas!"
                                </p>
                                <div className="h-1 w-20 bg-indigo-200 mx-auto rounded-full" />
                                <div className="flex justify-center items-center gap-4">
                                    <div className="bg-white px-6 py-2 rounded-2xl shadow-sm border border-indigo-50">
                                        <span className="text-xs font-black text-slate-400 uppercase block mb-1">Rezultatas</span>
                                        <span className="text-lg md:text-4xl font-black text-indigo-600">⭐ {score}</span>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => onComplete?.(score)}
                                className="premium-button-3d px-5 md:px-16 py-3 md:py-8 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl md:rounded-[3rem] font-black text-2xl shadow-2xl shadow-indigo-900/40 uppercase tracking-widest"
                            >
                                BAIGTI DEBATUS ✓
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DebateArenaGame;
